<template>
    <div id="image-settings">
        <div class="form-field">
            <label for="image-input" class="form-label">{{ $t("form.image") }}</label>
            <input type="file" id="image-input" class="form-input" @change="uploadFile" />
            <div v-if="logo">
                <img :src="logo" alt="Selected Image" style="max-width: 100%; margin-top: 10px;" />
            </div>
        </div>
        <div class="form-field">
            <label class="form-label">{{ $t("form.size") }}</label>
            <div class="size-settings">
                <div class="size-input">
                    <label for="width-input">{{ $t("form.width") }}</label>
                    <div class="input-with-unit">
                        <input type="number" id="width-input" v-model="imageWidth" class="form-input"
                               @input="updateImageWidth(imageWidth)" />
                        <span class="unit">PX</span>
                    </div>
                </div>
                <div class="size-input">
                    <label for="height-input">{{ $t("form.height") }}</label>
                    <div class="input-with-unit">
                        <input type="number" id="height-input" v-model="imageHeight" class="form-input"
                               @input="updateImageHeight" />
                        <span class="unit">PX</span>
                    </div>
                </div>
            </div>
        </div>
        <div class="form-field">
            <label class="form-label">{{ $t("form.imageAlignment") }}</label>
            <div class="button-group">
                <button class="button" :class="{ active: imageAlign === 'left' }" @click="updateImageAlign('left')">
                    {{ $t("form.left") }}
                </button>
                <button class="button" :class="{ active: imageAlign === 'center' }" @click="updateImageAlign('center')">
                    {{ $t("tables.center") }}
                </button>
                <button class="button" :class="{ active: imageAlign === 'right' }" @click="updateImageAlign('right')">
                    {{ $t("form.right") }}
                </button>
            </div>
        </div>
        
        <div class="form-field">
            <label class="form-label">Görünürlük</label>
            <div class="toggle">
                <span class="toggle-option" :class="{ active: visibility }"
                      @click="toggleVisibility">{{ visibility ? "Görünür" : "Gizli" }}</span>
            </div>
            <small class="form-description">Bu alanın form üzerinde görünür olup olmayacağını belirler</small>
        </div>

        <div class="form-field">
            <label for="slug-input" class="form-label">URL Parametresi</label>
            <input
                type="text"
                id="slug-input"
                class="form-input"
                placeholder="image"
                v-model="slug"
                @input="setSlug($event.target.value)"
            >
            <small class="form-description">URL parametrelerinde kullanılacak benzersiz tanımlayıcı</small>
        </div>
    </div>
</template>

<script>
import { uploadAsset } from "../../../../../api/assetUpload";
import axios from "axios";
import { mapActions, mapState } from "vuex";

export default {
    name: "ImageSettings",
    props: {
        item: Object,
    },
    data() {
        return {
            imageWidth: this.item.image.width || 150,
            imageHeight: this.item.image.height || 150,
            imageAlign: this.item.image.align || "center",
            logo: this.item.image.asset || "",
            visibility: this.item.visibility !== undefined ? this.item.visibility : true,
            slug: this.item.slug || '',
            updateTimer: null,
        };
    },
    computed: {
        ...mapState({
            site: (state) => state.site.site,
        }),
    },
    methods: {
        ...mapActions("forms", ["updateFormField"]),
        updateImageAlign(align) {
            this.imageAlign = align;
            this.$emit("update-image-align", align);
            this.updateField();
        },
        updateImageWidth(width) {
            this.imageWidth = width;
            this.$emit("update-image-width", width);
            this.updateField();
        },
        updateImageHeight() {
            this.$emit("update-image-height", this.imageHeight);
            this.updateField();
        },
        toggleVisibility() {
            this.visibility = !this.visibility;
            this.updateField();
            this.$emit('update-visibility', this.visibility);
        },
        setSlug(slug) {
            this.slug = this.sanitizeSlug(slug);
            this.updateField();
        },
        sanitizeSlug(slug) {
            if (!slug) return '';

            return slug
                .replace(/ğ/gim, "g")
                .replace(/ü/gim, "u")
                .replace(/ş/gim, "s")
                .replace(/ı/gim, "i")
                .replace(/ö/gim, "o")
                .replace(/ç/gim, "c")
                .replace(/[^a-z0-9-]/gim, "-")
                .replace(/-+/gim, "-")
                .replace(/^-|-$/gim, "")
                .toLowerCase();
        },
        async uploadFile(e) {
            let {
                files: [file],
            } = document.getElementById(e.target.id);
            if (!this.beforeUpload(file)) return;
            let path = `${this.site._id}/form/`;
            try {
                let imdUploadData = await uploadAsset({
                    path,
                    name: file.name,
                    size: file.size,
                    type: "form",
                    mime: file.type,
                    ext: file.name.split(".").pop(),
                });
                await axios({
                    method: imdUploadData.data.method,
                    url: imdUploadData.data.url,
                    data: file,
                    headers: { "Content-Type": "application/octet-stream" },
                });
                this.logo = imdUploadData.data.asset.v2Format && imdUploadData.data.asset.cdnUrl
                    ? imdUploadData.data.asset.cdnUrl
                    : `https://static.supsis.live/${imdUploadData.data.asset.path + imdUploadData.data.asset._id}.${imdUploadData.data.asset.ext}`;
                this.updateField();
                this.$emit("update-image-url", this.logo);
                this.$toasted.success(this.$t("message.successImgUpload"), {
                    duration: 1000,
                    position: "bottom-center",
                });

            } catch (e) {
                this.$toasted.error(this.$t("message.errorImgUpload"), {
                    duration: 1000,
                    position: "bottom-center",
                });
            }
        },
        beforeUpload(file) {
            const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/jpg" || file.type === "image/png";
            if (!isJpgOrPng) {
                this.$toasted.show(this.$t("form.onlyImageUpload"), {
                    duration: 3000,
                });
            }
            const isLt5M = file.size / 1024 / 1024 < 50;
            if (!isLt5M) {
                this.$toasted.show(this.$t("form.imageSizeLimit"), {
                    duration: 3000,
                });
            }
            return isJpgOrPng && isLt5M;
        },
        updateField() {
            let self = this;
            if (this.updateTimer) {
                clearTimeout(this.updateTimer);
            }
            this.updateTimer = setTimeout(function () {
                const updateFields = {
                    visibility: self.visibility,
                    slug: self.slug,
                    image: {
                        asset: self.logo,
                        width: self.imageWidth,
                        height: self.imageHeight,
                        align: self.imageAlign,
                    },
                };
                self.updateFormField({
                    siteId: self.item.siteId,
                    formFieldId: self.item._id,
                    updateFields: updateFields,
                });
            }, 800);
        },
    },
    watch: {
        item: {
            handler: function (val) {
                if (!val || !val.image) { return; }
                this.imageWidth = val.image.width || 150;
                this.imageHeight = val.image.height || 150;
                this.imageAlign = val.image.align || 'center';
                this.logo = val.image.asset || '';
                this.visibility = val.visibility !== undefined ? val.visibility : true;
                this.slug = val.slug || '';
            },
            deep: true,
            immediate: false,
        },
        'item.image.width': function (v) { this.imageWidth = v; },
        'item.image.height': function (v) { this.imageHeight = v; },
        'item.image.align': function (v) { this.imageAlign = v; },
        'item.image.asset': function (v) { this.logo = v || ''; },
        'item.visibility': function (v) { this.visibility = v; },
        'item.slug': function (v) { this.slug = v || ''; },
    },
};
</script>

<style lang="scss" scoped>
#image-settings {
    padding: 8px 12px 24px 12px;

    .form-field {
        display: flex;
        flex-direction: column;
        margin-bottom: 16px;

        .form-label {
            margin-bottom: 6px;
            font-weight: 600;
            color: #374151;
            font-size: 0.875rem;
            letter-spacing: 0.025em;
        }

        .form-input {
            padding: 8px 12px;
            border: 1px solid #d1d5db;
            border-radius: 8px;
            background-color: #ffffff;
            color: #374151;
            font-size: 0.875rem;
            transition: border-color 0.2s ease, box-shadow 0.2s ease;
            flex: 1;

            &:focus {
                outline: none;
                border-color: #f26a52;
                box-shadow: 0 0 0 3px rgba(242, 106, 82, 0.1);
            }

            &::placeholder {
                color: #9ca3af;
            }
        }

        .size-settings {
            display: flex;
            flex-direction: column;
            gap: 12px;
        }

        .size-input {
            .input-with-unit {
                display: flex;
                align-items: center;
                gap: 8px;

                input {
                    flex: 1;
                }

                .unit {
                    color: #6b7280;
                    font-size: 0.8rem;
                    font-weight: 500;
                    background: #f3f4f6;
                    padding: 4px 8px;
                    border-radius: 4px;
                    border: 1px solid #e5e7eb;
                }
            }
        }

        .button-group {
            display: flex;
            gap: 4px;
            margin-bottom: 8px;

            .button {
                flex: 1;
                padding: 6px 8px;
                border: 1px solid #e5e7eb;
                border-radius: 6px;
                background-color: #f9fafb;
                color: #6b7280;
                font-size: 0.8rem;
                font-weight: 500;
                cursor: pointer;
                text-align: center;
                transition: all 0.2s ease;

                &.active {
                    background-color: #f26a52;
                    color: #ffffff;
                    border-color: #f26a52;
                    box-shadow: 0 1px 3px rgba(242, 106, 82, 0.3);
                }

                &:hover:not(.active) {
                    background-color: #f3f4f6;
                    border-color: #d1d5db;
                }
            }
        }
    }
}
</style>
